import {
  useMutation,
  useQuery,
  type QueryClient,
  useQueryClient,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query";

import { withJWTClient } from "../connectClient";
import type { DragonTile } from "@/types/dragon-tiles";

import {
  dragonTilesKeys,
  dragonTilesLiteByTypeQueryOptions,
  getDragonTilesLite,
  updateDragonTile,
} from "@/api/dragon-tiles";
import {
  ConnectorConfigResponseSchema,
  type ConnectorEvent,
  type ConnectorTile,
  ExternalConnectorInstallResponseSchema,
  InternalConnectorInstallPayloadSchema,
  InstallableConnectorsResponseSchema,
  InstalledConnectorsResponseSchema,
  SaveConnectorConfigPayloadSchema,
  type ConnectorJsonFormsConfigData,
  type ConnectorConfigResponse,
  type ExternalConnectorInstallResponse,
  type InternalConnectorInstallPayload,
  type InstallableConnector,
  type InstalledConnector,
  type SaveConnectorConfigPayload,
} from "@/types/connectors";
import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

export const API_BASE_URL: string = import.meta.env?.DEV
  ? "/connectors"
  : (import.meta.env?.VITE_CONNECTORS_API_URL as string);

// Connectors catalog returns absolute install URLs for staging/prod integrations.
// Locally, prefer VITE_HOST_INTEGRATIONS_API_URL (e.g. http://127.0.0.1:18000/api/v1).
const LOCAL_INTEGRATIONS_API_BASE = (
  import.meta.env?.VITE_HOST_INTEGRATIONS_API_URL as string | undefined
)?.replace(/\/$/, "");

const INTEGRATIONS_HOST_RE =
  /^https:\/\/integrations\.(?:staging\.)?queen\.one\/api\/v1(?=\/|$)/;

export function resolveConnectorInstallationUrl(
  installationUrl: string,
  siteId: string,
): string {
  const withSite = installationUrl.replaceAll("{siteId}", siteId);
  if (!LOCAL_INTEGRATIONS_API_BASE) return withSite;
  return withSite.replace(INTEGRATIONS_HOST_RE, LOCAL_INTEGRATIONS_API_BASE);
}

/**
 * An installed connector, joined with its Dragon Tile when it has one.
 *
 * A connector with no subscribable events (a data hub such as Funnel.io) is
 * installed and configurable but owns no Dragon Tile, so `dragonTile`/`tile`
 * are absent. Settings surfaces list every installed connector; Dragon Tiles
 * surfaces narrow to the tiled subset — see `hasConnectorDragonTile` and
 * `isVisibleConnectorDragonTile`.
 */
export type HydratedConnector = {
  connectorId: string;
  installed: InstalledConnector;
  dragonTile?: DragonTile;
  tile?: ConnectorTile;
};

/** A hydrated connector that owns a Dragon Tile. */
export type HydratedConnectorDragonTile = HydratedConnector & {
  dragonTile: DragonTile;
  tile: ConnectorTile;
};

export function hasConnectorDragonTile(
  connector: HydratedConnector,
): connector is HydratedConnectorDragonTile {
  return !!connector.dragonTile && !!connector.tile;
}

export type ConnectorIntegrationEventState = {
  event: string;
  enabled: boolean;
};

// GET /{siteId}/installable

export async function getInstallableConnectors(
  siteId: string,
): Promise<InstallableConnector[]> {
  const response = await withJWTClient((client) =>
    client.get<unknown>(`${API_BASE_URL}/${siteId}/installable`),
  );
  const parseResult = InstallableConnectorsResponseSchema.safeParse(
    response.data,
  );
  if (!parseResult.success) {
    throw new Error("Installable connectors response schema mismatch.");
  }
  const parsedResponse = parseResult.data;

  const connectors = Array.isArray(parsedResponse)
    ? parsedResponse
    : "connectors" in parsedResponse
      ? parsedResponse.connectors
      : "installableConnectors" in parsedResponse
        ? parsedResponse.installableConnectors
        : parsedResponse.data;

  return connectors;
}

// GET /{siteId}/installed

export async function getInstalledConnectors(
  siteId: string,
): Promise<InstalledConnector[]> {
  const response = await withJWTClient((client) =>
    client.get<unknown>(`${API_BASE_URL}/${siteId}/installed`),
  );
  const parseResult = InstalledConnectorsResponseSchema.safeParse(
    response.data,
  );
  if (!parseResult.success) {
    throw new Error("Installed connectors response schema mismatch.");
  }
  const parsedResponse = parseResult.data;

  if (Array.isArray(parsedResponse)) {
    return parsedResponse;
  }

  if ("connectors" in parsedResponse) {
    return parsedResponse.connectors;
  }

  if ("installedConnectors" in parsedResponse) {
    return parsedResponse.installedConnectors;
  }

  return parsedResponse.data;
}

function mergeConnectorTileData(
  installed: InstalledConnector,
  dragonTile: DragonTile,
): ConnectorTile {
  return {
    ...dragonTile,
    name: installed.name ?? dragonTile.name,
    type: "connector",
    logoUrl: installed.logoUrl,
    events: installed.events,
  };
}

export async function getHydratedConnectors(
  siteId: string,
): Promise<HydratedConnector[]> {
  const [installed, connectorDragonTiles] = await Promise.all([
    getInstalledConnectors(siteId),
    getDragonTilesLite(siteId, "connector"),
  ]);
  return hydrateConnectors(installed, connectorDragonTiles);
}

/**
 * Left join: every installed connector survives, carrying its Dragon Tile when
 * one exists. An inner join here hid tile-less connectors from Settings
 * entirely — including their config page, which never even fetched.
 */
function hydrateConnectors(
  installed: InstalledConnector[],
  connectorDragonTiles: DragonTile[],
): HydratedConnector[] {
  const dragonTileByKey = new Map<string, DragonTile>();
  for (const tile of connectorDragonTiles) {
    dragonTileByKey.set(tile.name, tile);
  }

  return installed
    .map((connector) => {
      const dragonTile = dragonTileByKey.get(connector.id);
      return {
        connectorId: connector.id,
        installed: connector,
        dragonTile,
        tile: dragonTile
          ? mergeConnectorTileData(connector, dragonTile)
          : undefined,
      };
    })
    .sort(compareHydratedConnectors);
}

/**
 * Tiled connectors keep their Dragon Tile order; tile-less ones sort after them
 * by id, so the Settings list stays stable across refetches.
 */
function compareHydratedConnectors(
  a: HydratedConnector,
  b: HydratedConnector,
): number {
  if (a.tile && b.tile) return a.tile.order - b.tile.order;
  if (a.tile) return -1;
  if (b.tile) return 1;
  return a.connectorId.localeCompare(b.connectorId);
}

export async function getHydratedConnectorDragonTile(
  siteId: string,
  connectorId: string,
): Promise<HydratedConnectorDragonTile | null> {
  const hydrated = await getHydratedConnectors(siteId);
  return findHydratedConnectorTileById(hydrated, connectorId);
}

export function findHydratedConnectorById(
  hydratedConnectors: HydratedConnector[],
  connectorId: string,
): HydratedConnector | null {
  return (
    hydratedConnectors.find((item) => item.connectorId === connectorId) ?? null
  );
}

/** Dragon Tiles surfaces: resolves only connectors that own a tile. */
export function findHydratedConnectorTileById(
  hydratedConnectors: HydratedConnector[],
  connectorId: string,
): HydratedConnectorDragonTile | null {
  const connector = findHydratedConnectorById(hydratedConnectors, connectorId);
  return connector && hasConnectorDragonTile(connector) ? connector : null;
}

export function selectHydratedConnectorById(connectorId: string) {
  return (hydratedConnectors: HydratedConnector[]) =>
    findHydratedConnectorById(hydratedConnectors, connectorId);
}

export function selectHydratedConnectorTileById(connectorId: string) {
  return (hydratedConnectors: HydratedConnector[]) =>
    findHydratedConnectorTileById(hydratedConnectors, connectorId);
}

async function updateConnectorEvent(
  siteId: string,
  connectorId: string,
  payload: ConnectorIntegrationEventState,
): Promise<ConnectorIntegrationEventState> {
  const response = await withJWTClient((client) =>
    client.patch<ConnectorIntegrationEventState>(
      `${API_BASE_URL}/${siteId}/events/${connectorId}`,
      payload,
    ),
  );
  return response.data;
}

// PATCH /{siteId}/dragon-tiles/{dragonTileId}

export async function updateConnectorEnabled(
  siteId: string,
  connectorId: string,
  isEnabled: boolean,
): Promise<void> {
  const hydratedConnector = await getHydratedConnectorDragonTile(
    siteId,
    connectorId,
  );
  if (!hydratedConnector) {
    throw new Error(
      `Connector "${connectorId}" could not be mapped to a tile.`,
    );
  }

  await updateDragonTile(siteId, hydratedConnector.dragonTile.id, {
    id: hydratedConnector.dragonTile.id,
    is_active: isEnabled,
  });
}

// DELETE /{siteId}/installed/{connectorId}

export async function uninstallConnector(
  siteId: string,
  connectorId: string,
): Promise<void> {
  await withJWTClient((client) =>
    client.delete(`${API_BASE_URL}/${siteId}/uninstall/${connectorId}`),
  );
}

// GET /{siteId}/configs/{connectorId}

export async function getConnectorConfig(
  siteId: string,
  connectorId: string,
): Promise<ConnectorConfigResponse> {
  const response = await withJWTClient((client) =>
    client.get<ConnectorConfigResponse>(
      `${API_BASE_URL}/${siteId}/configs/${connectorId}`,
    ),
  );
  const parseResult = ConnectorConfigResponseSchema.safeParse(response.data);
  if (!parseResult.success) {
    throw new Error("Connector config response schema mismatch.");
  }
  return parseResult.data;
}

// POST /{siteId}/configs/{connectorId}
export async function saveConnectorConfig(
  siteId: string,
  connectorId: string,
  data: ConnectorJsonFormsConfigData,
): Promise<void> {
  const parseResult = SaveConnectorConfigPayloadSchema.safeParse(data);
  if (!parseResult.success) {
    throw new Error("Save connector config payload schema mismatch.");
  }
  const payload: SaveConnectorConfigPayload = parseResult.data;
  await withJWTClient((client) =>
    client.post(`${API_BASE_URL}/${siteId}/configs/${connectorId}`, payload),
  );
}

// GET /{installationUrl}

export async function getExternalConnectorInstallResponse(
  siteId: string,
  installationUrl: string,
): Promise<ExternalConnectorInstallResponse> {
  const resolvedUrl = resolveConnectorInstallationUrl(installationUrl, siteId);
  const response = await withJWTClient((client) =>
    client.get<ExternalConnectorInstallResponse>(resolvedUrl),
  );
  const parseResult = ExternalConnectorInstallResponseSchema.safeParse(
    response.data,
  );
  if (!parseResult.success) {
    throw new Error("External connector install response schema mismatch.");
  }
  return parseResult.data;
}

// POST /{installationUrl}

export async function submitInternalConnectorInstall(
  siteId: string,
  installationUrl: string,
  fields: InternalConnectorInstallPayload,
): Promise<unknown> {
  const resolvedUrl = resolveConnectorInstallationUrl(installationUrl, siteId);
  const parseResult = InternalConnectorInstallPayloadSchema.safeParse(fields);
  if (!parseResult.success) {
    throw new Error("Internal connector install payload schema mismatch.");
  }
  const parsedFields = parseResult.data;
  const response = await withJWTClient((client) =>
    client.post<unknown>(resolvedUrl, parsedFields),
  );
  return response.data;
}

// Query keys
export const connectorsKeys = {
  all: (siteId: string) => ["connectors", siteId] as const,
  installable: (siteId: string) =>
    [...connectorsKeys.all(siteId), "installable"] as const,
  installed: (siteId: string) =>
    [...connectorsKeys.all(siteId), "installed"] as const,
  hydrated: (siteId: string) =>
    [...connectorsKeys.all(siteId), "hydrated"] as const,
  config: (siteId: string, connectorId: string) =>
    [...connectorsKeys.all(siteId), "config", connectorId] as const,
};

// Query options
export const installableConnectorsQueryOptions = (siteId: string) => ({
  queryKey: connectorsKeys.installable(siteId),
  queryFn: () => getInstallableConnectors(siteId),
  enabled: !!siteId,
});

export const installedConnectorsQueryOptions = (siteId: string) => ({
  queryKey: connectorsKeys.installed(siteId),
  queryFn: () => getInstalledConnectors(siteId),
  /** Skip fetch when siteId is missing (e.g. `""` from optional route params). */
  enabled: !!siteId,
});

export const hydratedConnectorsQueryOptions = (
  siteId: string,
  queryClient?: QueryClient,
) => ({
  queryKey: connectorsKeys.hydrated(siteId),
  queryFn: async () => {
    if (!queryClient) {
      return getHydratedConnectors(siteId);
    }
    const [installed, connectorDragonTiles] = await Promise.all([
      // staleTime: 0 so this assembles from FRESH installed data. fetchQuery
      // honours the global 5-minute staleTime, so without it a deliberately
      // refetched hydrated query (the connector detail route sets
      // refetchOnMount: "always") would still be built from a cached list —
      // and `lastWebhookAt` would keep reporting "no webhooks received" for up
      // to five minutes after one arrived, which is the single signal that
      // panel exists to show. The hydrated query's own caching still governs
      // how often this queryFn runs at all.
      queryClient.fetchQuery({
        ...installedConnectorsQueryOptions(siteId),
        staleTime: 0,
      }),
      queryClient.fetchQuery(
        dragonTilesLiteByTypeQueryOptions(siteId, "connector"),
      ),
    ]);
    return hydrateConnectors(installed, connectorDragonTiles);
  },
  enabled: !!siteId,
});

export const connectorConfigQueryOptions = (
  siteId: string,
  connectorId: string,
) => ({
  queryKey: connectorsKeys.config(siteId, connectorId),
  queryFn: () => getConnectorConfig(siteId, connectorId),
  enabled: !!siteId && !!connectorId,
});

// React Query hooks
export function useInstallableConnectors(
  siteId: string,
  options?: Omit<
    UseQueryOptions<InstallableConnector[], Error>,
    "queryKey" | "queryFn" | "enabled"
  >,
): UseQueryResult<InstallableConnector[], Error> {
  return useQuery<InstallableConnector[], Error>({
    ...installableConnectorsQueryOptions(siteId),
    ...options,
  });
}

/**
 * Is the site's Shopify connector installed?
 *
 * Matched loosely on id/name until the canonical id is pinned (FOG-2498).
 *
 * This is the signal for "is this a Shopify store", in preference to
 * `site.platform` — that column is a legacy Rejoiner field nothing in the
 * Connect-era onboarding path writes, so it is empty for every brand. The
 * connector is also the more accurate question for anything that depends on
 * storefront scripts: it tracks the actual app install that puts them there.
 */
export function hasShopifyConnector(
  connectors: InstalledConnector[] | undefined,
): boolean {
  return (connectors ?? []).some((connector) =>
    `${connector.id} ${connector.name}`.toLowerCase().includes("shopify"),
  );
}

export function useInstalledConnectors(
  siteId: string,
  options?: Omit<
    UseQueryOptions<InstalledConnector[], Error>,
    "queryKey" | "queryFn" | "enabled"
  >,
): UseQueryResult<InstalledConnector[], Error> {
  return useQuery<InstalledConnector[], Error>({
    ...installedConnectorsQueryOptions(siteId),
    ...options,
  });
}

export function useHydratedConnectors<TData = HydratedConnector[]>(
  siteId: string,
  options?: Omit<
    UseQueryOptions<HydratedConnector[], Error, TData>,
    "queryKey" | "queryFn" | "enabled"
  >,
): UseQueryResult<TData, Error> {
  const queryClient = useQueryClient();
  return useQuery<HydratedConnector[], Error, TData>({
    ...hydratedConnectorsQueryOptions(siteId, queryClient),
    ...options,
  });
}

export function useHydratedConnector(
  siteId: string,
  connectorId: string,
  options?: Omit<
    UseQueryOptions<HydratedConnector[], Error, HydratedConnector | null>,
    "queryKey" | "queryFn" | "select"
  >,
): UseQueryResult<HydratedConnector | null, Error> {
  const isEnabled =
    !!siteId &&
    !!connectorId &&
    (options?.enabled === undefined || options.enabled);

  return useHydratedConnectors<HydratedConnector | null>(siteId, {
    select: selectHydratedConnectorById(connectorId),
    enabled: isEnabled,
    ...options,
  });
}

export function useInstalledConnector(
  siteId: string,
  connectorId: string,
  options?: Omit<
    UseQueryOptions<
      InstalledConnector[],
      Error,
      InstalledConnector | undefined
    >,
    "queryKey" | "queryFn" | "enabled" | "select"
  >,
): UseQueryResult<InstalledConnector | undefined, Error> {
  return useQuery<InstalledConnector[], Error, InstalledConnector | undefined>({
    ...installedConnectorsQueryOptions(siteId),
    select: (data) => data.find((connector) => connector.id === connectorId),
    enabled: !!siteId && !!connectorId,
    ...options,
  });
}

export function useConnectorConfig(
  siteId: string,
  connectorId: string,
  options?: Omit<
    UseQueryOptions<ConnectorConfigResponse, Error>,
    "queryKey" | "queryFn" | "enabled"
  >,
): UseQueryResult<ConnectorConfigResponse, Error> {
  return useQuery<ConnectorConfigResponse, Error>({
    ...connectorConfigQueryOptions(siteId, connectorId),
    ...options,
  });
}

export function useSaveConnectorConfig(siteId: string, connectorId: string) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, ConnectorJsonFormsConfigData>({
    mutationKey: [...connectorsKeys.config(siteId, connectorId), "save"],
    mutationFn: (data) => saveConnectorConfig(siteId, connectorId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: connectorsKeys.config(siteId, connectorId),
      });
    },
  });
}

export function useSetConnectorEnabled(siteId: string) {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { connectorId: string; isEnabled: boolean },
    {
      previousHydrated?: HydratedConnector[];
    }
  >({
    mutationKey: [...connectorsKeys.all(siteId), "updateEnabled"],
    mutationFn: ({ connectorId, isEnabled }) =>
      updateConnectorEnabled(siteId, connectorId, isEnabled),
    onMutate: async ({ connectorId, isEnabled }) => {
      const hydratedQueryKey = connectorsKeys.hydrated(siteId);

      await queryClient.cancelQueries({ queryKey: hydratedQueryKey });

      const previousHydrated =
        queryClient.getQueryData<HydratedConnector[]>(hydratedQueryKey);

      queryClient.setQueryData<HydratedConnector[]>(
        hydratedQueryKey,
        (current) =>
          current?.map((connector) => {
            // Enabled state lives on the Dragon Tile, so a tile-less connector
            // has none to flip.
            if (
              connector.connectorId !== connectorId ||
              !hasConnectorDragonTile(connector)
            ) {
              return connector;
            }
            return {
              ...connector,
              dragonTile: { ...connector.dragonTile, is_active: isEnabled },
              tile: { ...connector.tile, is_active: isEnabled },
            };
          }) ?? current,
      );

      return {
        previousHydrated,
      };
    },
    onError: (error, _variables, context) => {
      if (context?.previousHydrated) {
        queryClient.setQueryData(
          connectorsKeys.hydrated(siteId),
          context.previousHydrated,
        );
      }
      enqueueErrorSnackbar(
        error,
        "Failed to update connector status. Reverting changes.",
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: dragonTilesKeys.all(siteId),
        refetchType: "inactive",
      });
      void queryClient.invalidateQueries({
        queryKey: connectorsKeys.installed(siteId),
        refetchType: "inactive",
      });
      void queryClient.invalidateQueries({
        queryKey: connectorsKeys.hydrated(siteId),
        refetchType: "inactive",
      });
    },
  });
}

export function useUpdateConnectorEvent(siteId: string, connectorId: string) {
  const queryClient = useQueryClient();

  return useMutation<
    ConnectorIntegrationEventState,
    Error,
    ConnectorIntegrationEventState,
    {
      previousInstalled?: InstalledConnector[];
      previousHydrated?: HydratedConnector[];
    }
  >({
    mutationKey: [
      ...connectorsKeys.installed(siteId),
      connectorId,
      "eventUpdate",
    ],
    mutationFn: (payload) => updateConnectorEvent(siteId, connectorId, payload),
    onMutate: async (payload) => {
      const installedQueryKey = connectorsKeys.installed(siteId);
      const hydratedQueryKey = connectorsKeys.hydrated(siteId);

      await Promise.all([
        queryClient.cancelQueries({ queryKey: installedQueryKey }),
        queryClient.cancelQueries({ queryKey: hydratedQueryKey }),
      ]);

      const previousInstalled =
        queryClient.getQueryData<InstalledConnector[]>(installedQueryKey);
      const previousHydrated =
        queryClient.getQueryData<HydratedConnector[]>(hydratedQueryKey);

      const applyEventToggle = (events: ConnectorEvent[] | undefined) =>
        events?.map((eventItem) =>
          eventItem.id === payload.event
            ? { ...eventItem, enabled: payload.enabled }
            : eventItem,
        );

      queryClient.setQueryData<InstalledConnector[]>(
        installedQueryKey,
        (current) =>
          current?.map((connector) =>
            connector.id === connectorId
              ? {
                  ...connector,
                  events: applyEventToggle(connector.events),
                }
              : connector,
          ) ?? current,
      );
      queryClient.setQueryData<HydratedConnector[]>(
        hydratedQueryKey,
        (current) =>
          current?.map((connector) => {
            if (connector.connectorId !== connectorId) return connector;
            return {
              ...connector,
              installed: {
                ...connector.installed,
                events: applyEventToggle(connector.installed.events),
              },
              // A tile-less connector has no tile events to re-stamp.
              tile: connector.tile
                ? {
                    ...connector.tile,
                    events: applyEventToggle(connector.tile.events),
                  }
                : undefined,
            };
          }) ?? current,
      );

      return {
        previousInstalled,
        previousHydrated,
      };
    },
    onError: (error, _variables, context) => {
      if (context?.previousInstalled) {
        queryClient.setQueryData(
          connectorsKeys.installed(siteId),
          context.previousInstalled,
        );
      }
      if (context?.previousHydrated) {
        queryClient.setQueryData(
          connectorsKeys.hydrated(siteId),
          context.previousHydrated,
        );
      }
      enqueueErrorSnackbar(
        error,
        "Failed to update connector event. Reverting changes.",
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: connectorsKeys.installed(siteId),
        refetchType: "inactive",
      });
      void queryClient.invalidateQueries({
        queryKey: connectorsKeys.hydrated(siteId),
        refetchType: "inactive",
      });
    },
  });
}

export function useUninstallConnector(siteId: string) {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { connectorId: string },
    { previousInstalled?: InstalledConnector[] }
  >({
    mutationKey: [...connectorsKeys.all(siteId), "uninstall"],
    mutationFn: ({ connectorId }) => uninstallConnector(siteId, connectorId),
    onMutate: async ({ connectorId }) => {
      const installedQueryKey = connectorsKeys.installed(siteId);

      await queryClient.cancelQueries({ queryKey: installedQueryKey });

      const previousInstalled =
        queryClient.getQueryData<InstalledConnector[]>(installedQueryKey);

      queryClient.setQueryData<InstalledConnector[]>(
        installedQueryKey,
        (current) =>
          current?.filter((connector) => connector.id !== connectorId),
      );

      return { previousInstalled };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousInstalled) {
        queryClient.setQueryData(
          connectorsKeys.installed(siteId),
          context.previousInstalled,
        );
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: connectorsKeys.installed(siteId),
      });
      void queryClient.invalidateQueries({
        queryKey: connectorsKeys.installable(siteId),
      });
    },
  });
}
